/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Link, Typography, Typography2 } from '@coursera/cds-core';

import DegreesExperiments from 'bundles/epic/clients/Degrees';
import { TrackedA } from 'bundles/page/components/TrackedLink2';
import { Card } from 'bundles/program-loggedin-shared/components/Card';

import _t from 'i18n!nls/degree-home';

type Props = {
  supportEmailAddress: string;
  partnerId: number;
  supportLink?: string | null;
  degreeId: string;
};

const SupportInfoCard = ({ supportEmailAddress, partnerId, supportLink, degreeId }: Props): JSX.Element => {
  const helpLinkElm = (
    <Link
      component={TrackedA}
      href="https://learner.coursera.help/hc/"
      target="_blank"
      rel="noopener noreferrer"
      trackingName="help_link"
    >
      {_t('Coursera Learner Help Center')}
    </Link>
  );

  const renderSupportText = () => {
    if (supportLink) {
      return (
        <Typography2 component="p" variant="bodyPrimary">
          <FormattedMessage
            message={_t(`For any other questions regarding your program, click {supportLink}.`)}
            supportLink={
              <Link href={supportLink} target="_blank" rel="noopener noreferrer">
                {_t('here')}
              </Link>
            }
          />
        </Typography2>
      );
    } else {
      return (
        <Typography2 component="p" variant="bodyPrimary">
          <FormattedMessage
            message={_t('Contact {mailToLink} for any other questions regarding your program.')}
            mailToLink={<Link href={`mailto:${supportEmailAddress}`}>{supportEmailAddress}</Link>}
          />
        </Typography2>
      );
    }
  };

  const renderDegreeHomeSalesforceSSO = () => {
    return (
      DegreesExperiments.get('enableDegreeHomeSalesforceSSO', {
        // eslint-disable-next-line camelcase
        partner_id: partnerId.toString(),
      }) && (
        <Typography2
          component="p"
          variant="bodyPrimary"
          css={css`
            padding-bottom: 12px;
          `}
        >
          <FormattedMessage
            message={_t('You can chat with your program team directly {salesforceLink}.')}
            salesforceLink={
              <Link
                component={TrackedA}
                href="https://test.salesforce.com/services/auth/sso/00D21000000AKRhEAO/Degree_SSO_Test?community=https%3A%2F%2Fqastaging-coursera.cs26.force.com%2FStudentServicePortal"
                target="_blank"
                rel="noopener noreferrer"
                trackingName="salesforce_link"
              >
                {_t('here')}
              </Link>
            }
          />
        </Typography2>
      )
    );
  };

  const renderCardBody = () => {
    if (degreeId === 'base~orHWigmzRoC-Lmh6XTiltQ') {
      // UNT BAAS program hardcoded copy
      // https://coursera.atlassian.net/browse/CORDEG-1767
      return (
        <React.Fragment>
          <Typography2
            component="p"
            variant="bodyPrimary"
            css={css`
              padding-bottom: 12px;
              padding-top: 12px;
            `}
          >
            <FormattedMessage
              message={_t(
                'Visit the {helpLink} or email {degreeHelpLink} for technical support like submitting assignments, Coursera account settings, or other technical issues.'
              )}
              helpLink={helpLinkElm}
              degreeHelpLink={<Link href={`mailto:${supportEmailAddress}`}>{supportEmailAddress}</Link>}
            />
          </Typography2>
          {renderDegreeHomeSalesforceSSO()}
          <Typography2 component="p" variant="bodyPrimary">
            <FormattedMessage
              message={_t(
                'Contact {mailToLink} for any other questions regarding your program such as registration for classes and graduation.'
              )}
              mailToLink={<Link href="mailto:BAASCoursera@unt.edu">BAASCoursera@unt.edu</Link>}
            />
          </Typography2>
        </React.Fragment>
      );
    } else {
      return (
        // copy for other programs
        <React.Fragment>
          <Typography2
            component="p"
            variant="bodyPrimary"
            css={css`
              padding-bottom: 12px;
              padding-top: 12px;
            `}
          >
            <FormattedMessage
              message={_t(
                'Visit the {helpLink} for technical support like submitting assignments, Coursera account settings, or other technical issues.'
              )}
              helpLink={helpLinkElm}
            />
          </Typography2>
          {renderDegreeHomeSalesforceSSO()}
          {renderSupportText()}
        </React.Fragment>
      );
    }
  };

  return (
    <Card className="rc-SupportInfoCard pii-hide">
      <Typography component="h2" variant="h2semibold">
        {_t('Need Help?')}
      </Typography>

      {renderCardBody()}
    </Card>
  );
};

export default SupportInfoCard;
